import { TimelineLite, TweenLite } from "gsap";
import * as HLF from "html-living-framework";
import { Application } from "../Application";
import * as Constants from "../Data/Constants";
import { Culture } from "../Data/Culture";
import * as Topics from "../Data/Topics";
import * as VO from "../Data/VoiceOver";
import Gameplay from "../Gameplay";
import { InstrumentKey } from "./InstrumentKey";
import { KeyNoteParticleSystem } from "./KeyNoteParticleSystem";
import { InteruptType, VoiceCategory } from "./VoiceOverManager";

const BACKGROUND_IMAGE_PREFIX = "instrument-keyboard-bg-";
const FOREGROUND_IMAGE_PREFIX = "instrument-";
const SFX_PREFIX = "pad_";
const IDLE_TIMEOUT = 12000;
let VO_IDLE_TIMEOUT = 25000;
const VO_IDLE_TIMEOUT_2 = 45000;
const INSTRUMENT_NAMES = [
    ["Guitar", "Marimba", "Cajón", "Trumpet", "Violin", "Harp"], // mexico
    ["Tabla", "Edakka", "Sitar", "Kingri", "Bansuri", "Tambourine"], // india
    ["Sagat", "Ney", "Oud", "Rababa", "Mizmar", "Rubab"], // egypt
];
const INSTRUMENT_VOLUMES = [
    [1, 1, 2, 1, 2, 0.75], // mexico
    [2, 1, 1, 2, 2, 1], // india
    [0.75, 1, 1, 2, 1, 1], // egypt
];

export class InstrumentKeyboard extends PIXI.Container {

    public set inputEnabled(pEnabled: boolean) {
        for (let i = this._keys.length - 1; i >= 0; --i) {
            this._keys[i].interactive = pEnabled;
        }

        if (!pEnabled) {
            this.clearHintTimer();
        }
        else if (!this.hintTimeOut) {
            this.restartHintTimer();
        }
    }

    public set culture(pCulture: Culture) {
        this._cultureIndex = pCulture;

        const cultureAsString = HLF.NumberUtils.addZeroPadding(pCulture, 2);

        this._bg.texture = HLF.PixiUtils.getTexture(BACKGROUND_IMAGE_PREFIX + cultureAsString);

        this._noteParticleSystems.forEach((s) => {
            s.culture = pCulture;
        });
        this._keys.forEach((k) => {
            k.culture = pCulture;
        });
    }
    protected _hintTimeline?: TimelineLite;
    protected _introTimeline?: gsap.Animation;
    protected _backgroundFadeTimeline?: TweenLite;
    protected _messageTokens: any[];

    private readonly _keys: InstrumentKey[];
    private _size?: PIXI.Point;
    private _cultureIndex: Culture = Culture.Mexico;
    private readonly _bg: PIXI.Sprite;
    /** Duplicate of background image, for crossfading */
    private readonly _bg2: PIXI.Sprite;
    private readonly _noteParticleSystems: KeyNoteParticleSystem[];
    private hintTimeOut?: number;

    public get keys() { return this._keys; }

    constructor() {
        super();
        this._keys = new Array<InstrumentKey>();
        const cultureAsNumber = Gameplay.Instance.currentCulture as number;
        const cultureAsString = HLF.NumberUtils.addZeroPadding(cultureAsNumber, 2);

        this._bg = HLF.PixiUtils.makeSprite(BACKGROUND_IMAGE_PREFIX + cultureAsString);
        this._bg.anchor.set(0.5, 1.0);
        this.addChild(this._bg);

        this._bg2 = HLF.PixiUtils.makeSprite(BACKGROUND_IMAGE_PREFIX + cultureAsString);
        this._bg2.anchor.set(0.5, 1.0);
        this.addChild(this._bg2);

        if (Gameplay.Instance.currentCulture !== Culture.NumElements) {
            for (let i = 0; i < 6; i++) {
                const foregroundSprite = HLF.PixiUtils.makeSprite(
                    `${FOREGROUND_IMAGE_PREFIX}${i}-${cultureAsNumber}`,
                    Constants.SPRITESHEET_INSTRUMENT_KEYBOARD,
                );

                const audioFilename = `${SFX_PREFIX}${i}_${cultureAsNumber}_0`;

                this._keys.push(
                    new InstrumentKey(
                        () => {
                            //
                        },
                        foregroundSprite,
                        cultureAsNumber,
                        VO.INSTRUMENT_NAMES[Gameplay.Instance.currentCulture][i],
                        audioFilename,
                        INSTRUMENT_NAMES[cultureAsNumber][i],
                        INSTRUMENT_VOLUMES[cultureAsNumber][i],
                    ),
                );

                this._keys[i].pivot.set(0.5, 0.5);
                this._keys[i].scale.set(0.9, 0.9);
                this.addChild(this._keys[i]);
            }
        }

        this._noteParticleSystems = new Array<KeyNoteParticleSystem>();
        for (let i = 0; i < 6; i++) {
            this._noteParticleSystems.push(new KeyNoteParticleSystem(cultureAsNumber));
            this.addChild(this._noteParticleSystems[i]);
        }

        this._messageTokens = [
            PubSub.subscribe(Topics.CULTURE_CHANGE_BEGIN, this.onCultureChangeBegin.bind(this)),
            PubSub.subscribe(Topics.CULTURE_CHANGE_COMPLETE, this.onCultureChangeComplete.bind(this)),
            PubSub.subscribe(Topics.GAME_PAUSED, this.onPause.bind(this)),
            PubSub.subscribe(Topics.GAME_UNPAUSED, this.onResume.bind(this)),
            PubSub.subscribe(Topics.START_INTRO, this.startIntro.bind(this)),
            PubSub.subscribe(Topics.SAMPLE_PAD_BUTTON_CLICKED, this.playParticles.bind(this)),
        ];
    }

    public onResize(pSize: PIXI.Point): void {
        this._size = pSize.clone();

        this._bg.position.y = this._size.y * 0.5;
        this._bg2.position.y = this._size.y * 0.5;
        this.positionKeys(this._size);

        // this._buttonSwitchOrigin.position.set(0, this._size.y * 0.00);
    }

    public updateFromGame(pDeltaTime: number): void {
        for (let i = this._noteParticleSystems.length - 1; i >= 0; --i) {
            this._noteParticleSystems[i].updateFromGame(pDeltaTime);
        }
    }

    public onKeyPressed(pEvent: KeyboardEvent): void {
        if (pEvent.keyCode === HLF.Input.KeyCodes.Z) {
            this.playParticles(Topics.SAMPLE_PAD_BUTTON_CLICKED, this._keys[0]);
            this._keys[0].simulateClick();
        }
        else if (pEvent.keyCode === HLF.Input.KeyCodes.X) {
            this.playParticles(Topics.SAMPLE_PAD_BUTTON_CLICKED, this._keys[1]);
            this._keys[1].simulateClick();
        }
        else if (pEvent.keyCode === HLF.Input.KeyCodes.C) {
            this.playParticles(Topics.SAMPLE_PAD_BUTTON_CLICKED, this._keys[2]);
            this._keys[2].simulateClick();
        }
        else if (pEvent.keyCode === HLF.Input.KeyCodes.V) {
            this.playParticles(Topics.SAMPLE_PAD_BUTTON_CLICKED, this._keys[3]);
            this._keys[3].simulateClick();
        }
        else if (pEvent.keyCode === HLF.Input.KeyCodes.B) {
            this.playParticles(Topics.SAMPLE_PAD_BUTTON_CLICKED, this._keys[4]);
            this._keys[4].simulateClick();
        }
        else if (pEvent.keyCode === HLF.Input.KeyCodes.N) {
            this.playParticles(Topics.SAMPLE_PAD_BUTTON_CLICKED, this._keys[5]);
            this._keys[5].simulateClick();
        }
    }
    public playHint(withVO: boolean = false) {
        this._hintTimeline?.kill();
        if (!this._keys[0].interactive) {
            this.restartHintTimer();
            return;
        }
        if (withVO) {
            Application.instance.voiceOver.play(VO.O2, InteruptType.LESS, VoiceCategory.GAMEPLAY);
            VO_IDLE_TIMEOUT = VO_IDLE_TIMEOUT_2;
        }
        this._hintTimeline = new TimelineLite();
        this._hintTimeline
            .staggerTo(this._keys, 0.3, {pixi: {
                scaleX: 1.0,
                scaleY: 1.0,
            }}, 0.1, 0)
            .staggerTo(this._keys, 0.5, { pixi: {
                scaleX: 0.9,
                scaleY: 0.9,
            }}, 0.1, 0.5, () => {
                this.hintTimeOut = setTimeout(this.playHint.bind(this, true), VO_IDLE_TIMEOUT);
            });
    }

    public destroy(pOptions?: PIXI.DestroyOptions | boolean) {
        this.clearHintTimer();
        this._introTimeline?.kill();
        this._backgroundFadeTimeline?.kill();
        this._hintTimeline?.kill();
        this._messageTokens.forEach(PubSub.unsubscribe);
        PubSub.publishSync(HLF.UNREGISTER_FOCUSABLES, this._keys);
        super.destroy(pOptions);
    }

    protected startIntro() {
        this._introTimeline = TweenLite
            .fromTo(this, 1, {pixi: {positionY: this._size!.y}}, {pixi: {positionY: 0}})
            .delay(1)
            .eventCallback(Constants.TWEEN_ONCOMPLETE, this.registerFocusables.bind(this))
        ;
    }

    protected registerFocusables(): void {
        PubSub.publishSync(HLF.REGISTER_FOCUSABLES, this._keys);
    }

    protected onPause(pTopic: string): void {
        this.clearHintTimer();
        this._introTimeline?.pause();
        this._backgroundFadeTimeline?.pause();
        this._hintTimeline?.kill();
    }
    protected onResume(pTopic: string): void {
        if (this._introTimeline?.paused()) {
            this._introTimeline?.resume();
        }
        if (this._backgroundFadeTimeline?.paused()) {
            this._backgroundFadeTimeline.resume();
        }
        this.restartHintTimer();
    }

    private clearHintTimer() {
        clearTimeout(this.hintTimeOut);
        this.hintTimeOut = undefined;
    }

    private startHintTimer() {
        this.hintTimeOut = setTimeout(this.playHint.bind(this), IDLE_TIMEOUT);
    }

    private restartHintTimer() {
        this.clearHintTimer();
        this.startHintTimer();
    }

    private positionKeys(pSize: PIXI.Point): void {
        let widthOffset: number = 0;
        let heightOffset: number = 0;
        let x: number = 0;
        let y: number = 0;
        for (let i = 0; i < 6; i++) {
            widthOffset = this._keys[i].iconSize.x * 0.5 + 5;
            heightOffset = this._keys[i].iconSize.y * 0.5 - 2;
            x = -Constants.MIN_SCREEN_HALF_WIDTH + (this._keys[i].iconSize.x * 0.95) +
            ((this._keys[i].iconSize.x * 0.915) * i);
            y = pSize.y * 0.5 - pSize.y * 0.19 + heightOffset * 0.5;
            this._keys[i].position.set(x, y);
            this._noteParticleSystems[i].position.set(x, y);
        }
    }

    private playParticles(pTopic: string, pKey: InstrumentKey): void {
        // Play particle system
        this._noteParticleSystems[this._keys.indexOf(pKey)].startEmitting(this._cultureIndex);
        this.restartHintTimer();
    }

    private onCultureChangeComplete(pTopic: string, pCulture: Culture): void {
        this.culture = pCulture;
        this._bg.alpha = 1;
        this._bg2.alpha = 0;
        this.restartHintTimer();
    }

    private onCultureChangeBegin(pTopic: string, pCulture: Culture): void {
        if (pCulture !== Culture.NumElements) {
            const cultureAsString = HLF.NumberUtils.addZeroPadding(pCulture, 2);
            this._bg2.texture = HLF.PixiUtils.getTexture(BACKGROUND_IMAGE_PREFIX + cultureAsString);
            this._bg.alpha = 1;
            this._bg2.alpha = 0;
            this._backgroundFadeTimeline = TweenLite.to(this._bg2, 1, {alpha: 1});
            this.clearHintTimer();
            for (let i = 0; i < this._keys.length; i++) {
                this._keys[i].switchCulture(
                    pCulture,
                    INSTRUMENT_NAMES[pCulture][i],
                    HLF.PixiUtils.getTexture(
                        `${FOREGROUND_IMAGE_PREFIX}${i}-${pCulture}`,
                        Constants.SPRITESHEET_INSTRUMENT_KEYBOARD,
                    ),
                    `${SFX_PREFIX}${i}_${pCulture}_0`,
                    INSTRUMENT_VOLUMES[pCulture][i],
                    VO.INSTRUMENT_NAMES[pCulture][i],
                    i * 0.25,
                );
            }
        }
    }
}
